import axios, { AxiosInstance, AxiosError } from 'axios'

// Determine API base URL based on environment
// Strategy:
// 1. Development: Use relative path to leverage Vite proxy
// 2. Production with VITE_API_URL: Use provided URL (for separate Worker deployment)
// 3. Production without VITE_API_URL: Use relative path (for Pages Functions integration)
const isDevelopment = import.meta.env.DEV
const providedApiUrl = import.meta.env.VITE_API_URL

// If VITE_API_URL is provided, use it (for separate Worker deployment)
// Otherwise, use relative path (works for both dev proxy and Pages Functions)
const baseURL = providedApiUrl 
  ? `${providedApiUrl}/api/v1`
  : '/api/v1'

class ApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json'
      },
      timeout: 10000 // 10 second timeout
    })

    // Error interceptor untuk consistent error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => {
        // Enhanced error message for network errors
        if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK' || !error.response) {
          let errorMessage = 'Backend server tidak dapat diakses.'
          
          if (error.code === 'ECONNREFUSED') {
            errorMessage = 'Backend server tidak berjalan. Silakan jalankan: npm run dev (untuk backend)'
          } else if (error.message?.includes('timeout')) {
            errorMessage = 'Backend server timeout. Pastikan server berjalan di port 8787.'
          } else {
            errorMessage = `Network Error: ${error.message || 'Tidak dapat terhubung ke backend server'}`
          }
          
          const enhancedError = new Error(errorMessage)
          enhancedError.name = 'NetworkError'
          console.error('API Connection Error:', {
            code: error.code,
            message: error.message,
            baseURL
          })
          return Promise.reject(enhancedError)
        }
        
        // Handle backend errors (like D1 database errors)
        const backendError = error.response?.data as any
        if (backendError?.error) {
          const enhancedError = new Error(
            backendError.error.includes('1031') 
              ? 'Database tidak terkonfigurasi. Pastikan D1 database sudah di-setup dengan: wrangler d1 execute landscape-pm --remote'
              : backendError.error
          )
          enhancedError.name = 'BackendError'
          console.error('Backend Error:', backendError)
          return Promise.reject(enhancedError)
        }
        
        console.error('API Error:', error.response?.data || error.message)
        return Promise.reject(error)
      }
    )
  }

  // BoQ endpoints
  getBoQByProject(projectId: number) {
    return this.client.get(`/boq/projects/${projectId}`)
  }

  createBoQCategory(data: any) {
    return this.client.post('/boq/categories', data)
  }

  updateBoQCategory(categoryId: number, data: any) {
    return this.client.patch(`/boq/categories/${categoryId}`, data)
  }

  deleteBoQCategory(categoryId: number) {
    return this.client.delete(`/boq/categories/${categoryId}`)
  }

  getBoQItem(itemId: number) {
    return this.client.get(`/boq/items/${itemId}`)
  }

  createBoQItem(data: any) {
    return this.client.post('/boq/items', data)
  }

  updateBoQItem(itemId: number, data: any) {
    return this.client.patch(`/boq/items/${itemId}`, data)
  }

  deleteBoQItem(itemId: number) {
    return this.client.delete(`/boq/items/${itemId}`)
  }

  batchUpdateBoQItems(updates: Array<{ id: number; data: any }>) {
    return this.client.patch('/boq/items/batch/update', { updates })
  }

  // Projects endpoints
  getProjects() {
    return this.client.get('/projects')
  }

  getProject(projectId: number) {
    return this.client.get(`/projects/${projectId}`)
  }

  createProject(data: any) {
    return this.client.post('/projects', data)
  }

  updateProject(projectId: number, data: any) {
    return this.client.patch(`/projects/${projectId}`, data)
  }

  deleteProject(projectId: number) {
    return this.client.delete(`/projects/${projectId}`)
  }

  // Curva S endpoints
  getTimePeriods(projectId: number, periodType?: string) {
    let url = `/curva/projects/${projectId}/periods`
    if (periodType) url += `?type=${periodType}`
    return this.client.get(url)
  }

  generateTimePeriods(projectId: number, type: 'week' | 'day', deleteExisting?: boolean) {
    return this.client.post(`/curva/projects/${projectId}/periods/generate`, {
      type,
      deleteExisting
    })
  }

  deleteTimePeriods(projectId: number) {
    return this.client.delete(`/curva/projects/${projectId}/periods`)
  }

  getCurvaSData(projectId: number) {
    return this.client.get(`/curva/projects/${projectId}/data`)
  }

  updatePlannedWeight(itemId: number, periodId: number, plannedWeight: number) {
    return this.client.patch(
      `/curva/items/${itemId}/periods/${periodId}/planned-weight`,
      { plannedWeight }
    )
  }

  updateActualWeight(itemId: number, periodId: number, actualWeight: number) {
    return this.client.patch(
      `/curva/items/${itemId}/periods/${periodId}/actual-weight`,
      { actualWeight }
    )
  }

  exportCurvaSData(projectId: number) {
    return this.client.get(`/curva/projects/${projectId}/export`, {
      responseType: 'text'
    })
  }

  importCurvaSData(projectId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.client.post(`/curva/projects/${projectId}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // BoQ import endpoint
  importBoQData(projectId: number, file: File) {
    const formData = new FormData()
    formData.append('file', file)
    return this.client.post(`/boq/projects/${projectId}/import`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  }

  // Task endpoints
  getTasks(projectId: number) {
    return this.client.get(`/tasks/projects/${projectId}`)
  }

  generateTasksFromBoQ(projectId: number, deleteExisting?: boolean) {
    return this.client.post('/tasks/generate-from-boq', {
      projectId,
      deleteExisting
    })
  }

  getPerformanceKPI(projectId: number) {
    return this.client.get(`/tasks/projects/${projectId}/performance-kpi`)
  }

  getCriticalPath(projectId: number) {
    return this.client.get(`/tasks/projects/${projectId}/critical-path`)
  }

  updateTaskStatus(taskId: number, status: string) {
    return this.client.patch(`/tasks/${taskId}/status`, { status })
  }

  updateTaskResources(taskId: number, resources: any) {
    return this.client.patch(`/tasks/${taskId}/resources`, { resources })
  }

  calculateRescheduleImpact(taskId: number, delayDays: number, costPerDay?: number) {
    return this.client.post(
      `/tasks/${taskId}/calculate-reschedule-impact`,
      { delayDays, costPerDay }
    )
  }

  createTask(data: any) {
    return this.client.post('/tasks', data)
  }

  deleteTask(taskId: number) {
    return this.client.delete(`/tasks/${taskId}`)
  }
}

export const apiClient = new ApiClient()

